import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import EmailIcon from '@mui/icons-material/Email';
import { useCart } from '../contexts/CartContext';

const CartDrawer = () => {
  const {
    cartItems,
    isCartOpen,
    setIsCartOpen,
    updateQuantity,
    removeFromCart,
    clearCart,
    getCartPriceTotal,
  } = useCart();
  const [checkoutDialog, setCheckoutDialog] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
  });

  const handleClose = () => {
    setIsCartOpen(false);
  };

  const handleCheckout = () => {
    setCheckoutDialog(true);
  };

  const handleCloseCheckout = () => {
    setCheckoutDialog(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      notes: '',
    });
  };

  const handleFormChange = e => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmitOrder = async () => {
    const items = cartItems
      .map(
        item =>
          `${item.name}${item.size ? ` (Size: ${item.size})` : ''} - Quantity: ${item.quantity}`
      )
      .join('\n');

    // OPTION 1: Discord Webhook (Easiest - just paste URL)
    // Get webhook: Discord Server Settings > Integrations > Webhooks > New Webhook
    const DISCORD_WEBHOOK =
      'https://discord.com/api/webhooks/1467203338700587160/w5Kk2TSxKg0nVKExpcYyemiF8n-dZuF4zq0AMr6Mo3EnwvIkbr1F731sGxl3v8QWrYuJ'; // Paste Discord webhook URL here

    // OPTION 2: Telegram Bot (Alternative)
    const TELEGRAM_BOT_TOKEN = ''; // Telegram bot token
    const TELEGRAM_CHAT_ID = ''; // Your chat ID

    try {
      let sent = false;

      // Try Discord first
      if (DISCORD_WEBHOOK) {
        await fetch(DISCORD_WEBHOOK, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            embeds: [
              {
                title: '🛒 New Order',
                color: 16766720,
                fields: [
                  { name: '👤 Customer', value: formData.name, inline: true },
                  { name: '📧 Email', value: formData.email, inline: true },
                  { name: '📱 Phone', value: formData.phone || 'N/A', inline: true },
                  { name: '🛍️ Items', value: items },
                  { name: '📝 Notes', value: formData.notes || 'None' },
                ],
                timestamp: new Date().toISOString(),
              },
            ],
          }),
        });
        sent = true;
      }
      // Try Telegram
      else if (TELEGRAM_BOT_TOKEN && TELEGRAM_CHAT_ID) {
        const message = `🛒 *New Order*\n\n👤 ${formData.name}\n📧 ${formData.email}\n📱 ${formData.phone || 'N/A'}\n\n🛍️ *Items:*\n${items}\n\n📝 ${formData.notes || 'None'}`;
        await fetch(`https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendMessage`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chat_id: TELEGRAM_CHAT_ID,
            text: message,
            parse_mode: 'Markdown',
          }),
        });
        sent = true;
      }

      if (sent) {
        // Show payment dialog
        handleCloseCheckout();
        setPaymentDialog(true);
      } else {
        // Fallback to email
        const subject = 'Order Request from Mirmire Shop';
        const body = `Name: ${formData.name}%0D%0AEmail: ${formData.email}%0D%0APhone: ${formData.phone}%0D%0A%0D%0AItems:%0D%0A${items.replace(/\n/g, '%0D%0A')}%0D%0A%0D%0ANotes:%0D%0A${formData.notes}`;
        window.location.href = `mailto:mirmiresc@gmail.com?subject=${subject}&body=${body}`;
        handleCloseCheckout();
        setPaymentDialog(true);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to send order. Please try again.');
    }
  };

  const handlePaymentComplete = () => {
    clearCart();
    setPaymentDialog(false);
    handleClose();
  };

  return (
    <>
      <Drawer anchor="right" open={isCartOpen} onClose={handleClose}>
        <Box
          sx={{
            width: { xs: '100vw', sm: 400 },
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#FFFFFF',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              bgcolor: '#000000',
              color: '#FFD700',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCartIcon sx={{ fontSize: '1.8rem', color: '#FFFFFF' }} />
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, fontSize: '1.5rem', color: '#FFFFFF' }}
              >
                Your Cart ({cartItems.length})
              </Typography>
            </Box>
            <IconButton onClick={handleClose} sx={{ color: '#FFD700' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Cart Items */}
          <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
            {cartItems.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#666666',
                }}
              >
                <ShoppingCartIcon sx={{ fontSize: '4rem', mb: 2, opacity: 0.3 }} />
                <Typography variant="h6">Your cart is empty</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Add some items to get started!
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {cartItems.map(item => (
                  <React.Fragment key={`${item.id}-${item.size || 'no-size'}`}>
                    <ListItem
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        p: 0,
                        gap: 0,
                        bgcolor: '#FFFFFF',
                        border: '2px solid #FFD700',
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(218, 165, 32, 0.15)',
                      }}
                    >
                      {/* Item content with image */}
                      <Box sx={{ display: 'flex', gap: 2, p: 2 }}>
                        {/* Product Image */}
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            bgcolor: '#F5F5F5',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            border: '1px solid #E0E0E0',
                            overflow: 'hidden',
                          }}
                        >
                          <img
                            src={item.image}
                            alt={item.name}
                            onError={e => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: '3rem',
                              display: 'none',
                            }}
                          >
                            {item.fallbackIcon}
                          </Typography>
                        </Box>

                        {/* Item Details */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, color: '#000000' }}
                          >
                            {item.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: '#DAA520', fontWeight: 600, fontSize: '0.85rem' }}
                          >
                            €{item.price ? item.price.toFixed(2) : '0.00'}
                          </Typography>
                          {item.size && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#666666',
                                bgcolor: '#FFF9E6',
                                px: 1,
                                py: 0.25,
                                borderRadius: 0.5,
                                width: 'fit-content',
                                fontSize: '0.85rem',
                              }}
                            >
                              Size: {item.size}
                            </Typography>
                          )}
                        </Box>

                        {/* Delete Button */}
                        <IconButton
                          size="small"
                          onClick={() => removeFromCart(item.id, item.size)}
                          sx={{
                            color: '#DAA520',
                            height: 'fit-content',
                            '&:hover': {
                              color: '#FFD700',
                              bgcolor: 'rgba(255, 215, 0, 0.1)',
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>

                      {/* Quantity Controls */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 2,
                          pt: 0,
                        }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ color: '#666666', fontWeight: 500 }}>
                            Quantity:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: '#000000', fontWeight: 700, fontSize: '0.9rem', mt: 0.5 }}
                          >
                            Subtotal: €
                            {item.price ? (item.price * item.quantity).toFixed(2) : '0.00'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => updateQuantity(item.id, item.size, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            sx={{
                              border: '1.5px solid #DAA520',
                              color: '#000000',
                              width: 32,
                              height: 32,
                              '&:hover': {
                                bgcolor: 'rgba(218, 165, 32, 0.1)',
                                borderColor: '#FFD700',
                              },
                              '&:disabled': {
                                borderColor: '#E0E0E0',
                                color: '#999999',
                              },
                            }}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <Typography
                            sx={{
                              minWidth: '40px',
                              textAlign: 'center',
                              fontWeight: 700,
                              color: '#000000',
                              fontSize: '1.2rem',
                            }}
                          >
                            {item.quantity}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => updateQuantity(item.id, item.size, item.quantity + 1)}
                            sx={{
                              border: '1.5px solid #DAA520',
                              color: '#000000',
                              width: 32,
                              height: 32,
                              '&:hover': {
                                bgcolor: 'rgba(218, 165, 32, 0.1)',
                                borderColor: '#FFD700',
                              },
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>

          {/* Footer with actions */}
          {cartItems.length > 0 && (
            <Box
              sx={{
                p: 2,
                borderTop: '2px solid #FFD700',
                bgcolor: '#000000',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                  pb: 2,
                  borderBottom: '1px solid #FFD700',
                }}
              >
                <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                  Total:
                </Typography>
                <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700 }}>
                  €{getCartPriceTotal().toFixed(2)}
                </Typography>
              </Box>
              <Button
                variant="contained"
                fullWidth
                onClick={handleCheckout}
                startIcon={<EmailIcon />}
                sx={{
                  bgcolor: '#FFD700',
                  color: '#000000',
                  py: 1.5,
                  fontWeight: 600,
                  mb: 1.5,
                  fontSize: '1rem',
                  '&:hover': {
                    bgcolor: '#DAA520',
                    color: '#000000',
                  },
                }}
              >
                Proceed to Checkout
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={clearCart}
                sx={{
                  color: '#FFD700',
                  borderColor: '#FFD700',
                  borderWidth: 2,
                  '&:hover': {
                    borderColor: '#DAA520',
                    borderWidth: 2,
                    bgcolor: 'rgba(255, 215, 0, 0.1)',
                    color: '#DAA520',
                  },
                }}
              >
                Clear Cart
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Checkout Dialog */}
      <Dialog open={checkoutDialog} onClose={handleCloseCheckout} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            bgcolor: '#000000',
            color: '#FFD700',
            fontWeight: 600,
          }}
        >
          Complete Your Order
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 3, color: '#666666' }}>
            Fill in your details and we'll get back to you shortly about your order.
          </Typography>

          {/* Order Summary */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: '#FFF9E6',
              borderRadius: 1,
              border: '1px solid #FFD700',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#000000' }}>
              Order Summary:
            </Typography>
            {cartItems.map(item => (
              <Box
                key={`${item.id}-${item.size || 'no-size'}`}
                sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}
              >
                <Typography variant="body2" sx={{ color: '#666666' }}>
                  • {item.name}
                  {item.size && ` (${item.size})`} × {item.quantity}
                </Typography>
                <Typography variant="body2" sx={{ color: '#000000', fontWeight: 600 }}>
                  €{item.price ? (item.price * item.quantity).toFixed(2) : '0.00'}
                </Typography>
              </Box>
            ))}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 2,
                pt: 2,
                borderTop: '2px solid #DAA520',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#000000' }}>
                Total:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#DAA520' }}>
                €{getCartPriceTotal().toFixed(2)}
              </Typography>
            </Box>
          </Box>

          <TextField
            margin="dense"
            name="name"
            label="Your Name"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleFormChange}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="email"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={formData.email}
            onChange={handleFormChange}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="phone"
            label="Phone Number"
            type="tel"
            fullWidth
            variant="outlined"
            value={formData.phone}
            onChange={handleFormChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="notes"
            label="Additional Notes"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={formData.notes}
            onChange={handleFormChange}
            placeholder="Any special requests or comments..."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseCheckout}
            sx={{
              color: '#666666',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitOrder}
            variant="contained"
            disabled={!formData.name || !formData.email}
            sx={{
              bgcolor: '#000000',
              color: '#FFD700',
              '&:hover': {
                bgcolor: '#DAA520',
                color: '#000000',
              },
              '&:disabled': {
                bgcolor: '#E0E0E0',
                color: '#999999',
              },
            }}
          >
            Send Order Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* MobilePay Payment Dialog */}
      <Dialog
        open={paymentDialog}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#FFFFFF',
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#000000',
            color: '#FFD700',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontWeight: 'bold',
          }}
        >
          💳 Complete Your Payment
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 2, mt: 3, color: '#000000', fontWeight: 'bold' }}>
              Pay with MobilePay
            </Typography>

            <Typography sx={{ mb: 2, color: '#666666' }}>
              Scan the QR code below with your MobilePay app to complete the payment
            </Typography>

            {/* Total Amount */}
            <Box
              sx={{
                bgcolor: '#000000',
                borderRadius: 2,
                p: 2,
                mb: 3,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                Total Amount:
              </Typography>
              <Typography variant="h5" sx={{ color: '#FFD700', fontWeight: 700 }}>
                €{getCartPriceTotal().toFixed(2)}
              </Typography>
            </Box>

            {/* QR Code */}
            <Box
              sx={{
                width: 250,
                height: 250,
                margin: '0 auto',
                mb: 3,
                bgcolor: '#F5F5F5',
                border: '2px solid #FFD700',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src="/merch/payment.jpeg"
                alt="MobilePay QR Code"
                style={{ maxWidth: '100%', maxHeight: '100%' }}
              />
            </Box>

            <Box
              sx={{
                bgcolor: '#FFF9E6',
                border: '1px solid #FFD700',
                borderRadius: 2,
                p: 2.5,
                mb: 2,
              }}
            >
              <Typography variant="body1" sx={{ color: '#000000', fontWeight: 'bold', mb: 1 }}>
                ✅ Order Received!
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666' }}>
                We will contact you for delivery details after payment is confirmed.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handlePaymentComplete}
            variant="contained"
            fullWidth
            sx={{
              bgcolor: '#000000',
              color: '#FFD700',
              py: 1.5,
              fontWeight: 'bold',
              '&:hover': {
                bgcolor: '#DAA520',
                color: '#000000',
              },
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CartDrawer;
